import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Import new UI components (using Components with capital C to match existing folder)
import { ToastProvider, useToast } from '../Components/ui/Toast';
import { BottomSheet } from '../Components/ui/BottomSheet';
import { PersonForm } from '../Components/person/PersonForm';
import { PersonDetailSheet } from '../Components/person/PersonDetailSheet';
import { GiftForm } from '../Components/gift/GiftForm';
import { GiftDiscoverySheet } from '../Components/gift/GiftDiscovery';
import { TimelineView } from '../Components/dashboard/TimelineView';
import { useConfetti, CelebrationOverlay } from '../Components/ui/Confetti';
import { LogoIcon } from '../Components/ui/Logo';

/**
 * NewHomePage - Showcase of the revolutionary new design
 *
 * This demonstrates the new Giftedict UX with:
 * - Timeline dashboard
 * - Bottom navigation
 * - Bottom sheets
 * - Celebration animations
 */

// API URL for backend
const API_URL = process.env.REACT_APP_API_URL || 'https://giftedict-backend.onrender.com';

function NewHomePageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { trigger: triggerConfetti, Confetti } = useConfetti();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetType, setSheetType] = useState(null); // 'person' | 'gift' | 'menu' | 'discovery' | 'personDetail'
  const [showCelebration, setShowCelebration] = useState(false);
  const [people, setPeople] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('home'); // 'home' | 'people' | 'calendar' | 'gifts-sent'
  const [selectedPerson, setSelectedPerson] = useState(null); // For gift discovery flow
  const [editingPerson, setEditingPerson] = useState(null); // For edit mode
  const [viewingPerson, setViewingPerson] = useState(null); // For person detail view with suggestions

  // Fetch user info and gifts on mount
  useEffect(() => {
    const token = localStorage.getItem('authtoken');
    if (!token) {
      navigate('/signin');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user info
        const userResponse = await fetch(`${API_URL}/api/auth/getUserDetails`, {
          headers: { 'auth-token': token }
        });
        const userData = await userResponse.json();
        if (userData.success && userData.data) {
          setUserName(userData.data.name || '');
          setUserEmail(userData.data.email || '');
          // Check if user is admin and redirect to admin dashboard
          const adminEmails = ['dayagnik@gmail.com', 'desaidev@gmail.com'];
          const userIsAdmin = adminEmails.includes(userData.data.email);
          setIsAdmin(userIsAdmin);
          if (userIsAdmin) {
            navigate('/admin');
            return;
          }
        }

        // Fetch user's gifts/people
        const giftsResponse = await fetch(`${API_URL}/api/gifts/getgifts`, {
          headers: { 'auth-token': token }
        });
        const giftsData = await giftsResponse.json();
        if (Array.isArray(giftsData)) {
          // Separate people (with dates for occasions) from gifts
          setPeople(giftsData);
          setGifts(giftsData);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load your data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, toast]);

  // Handle Google OAuth token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Store the token and clean up URL
      localStorage.setItem('authtoken', token);
      // Remove token from URL without reload
      window.history.replaceState({}, document.title, '/dashboard');
      toast.success('Welcome! You\'ve signed in with Google.');
    }
  }, [searchParams, toast]);

  // Handle sign out
  const handleSignOut = () => {
    localStorage.removeItem('authtoken');
    setUserMenuOpen(false);
    toast.success('Signed out successfully');
    navigate('/');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuOpen && !e.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  // Open add menu
  const handleFabClick = () => {
    setSheetType('menu');
    setSheetOpen(true);
  };

  // Handle add person
  const handleAddPerson = () => {
    setSheetType('person');
  };

  // Handle add gift
  const handleAddGift = () => {
    setSheetType('gift');
  };

  // Handle person form complete - save to database (add or update)
  const handlePersonComplete = async (data, editId) => {
    // Close the sheet immediately
    handleCloseSheet();
    const isEditing = !!editId;

    const token = localStorage.getItem('authtoken');
    if (!token) {
      toast.error('Please sign in to add people');
      return;
    }

    try {
      const url = isEditing
        ? `${API_URL}/api/gifts/updateGift/${editId}`
        : `${API_URL}/api/gifts/addgift`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({
          recipientName: data.recipientName,
          relationship: data.relationship,
          relationshipDetail: data.relationshipDetail || '',
          occasion: data.occasion,
          date: data.date,
          interests: Array.isArray(data.interests) ? data.interests.join(', ') : (data.interests || ''),
          budget: data.budget || '',
          reminderDays: data.reminderDays || 7,
          gender: data.gender || '',
          address: data.address || '',
          phoneNumber: data.phoneNumber || '',
          email: data.email || '',
          comments: data.comments || '',
          defaultOption: data.defaultOption || ''
        })
      });

      const result = await response.json();
      if (result.success) {
        // Refresh data from server
        const giftsResponse = await fetch(`${API_URL}/api/gifts/getgifts`, {
          headers: { 'auth-token': token }
        });
        const giftsData = await giftsResponse.json();
        if (Array.isArray(giftsData)) {
          setPeople(giftsData);
          setGifts(giftsData);
        }

        toast.success(isEditing ? `${data.recipientName} updated!` : `${data.recipientName} added!`);
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } catch (error) {
      console.error('Error saving person:', error);
      toast.error('Failed to save. Please try again.');
    }

    // Clear editing state
    setEditingPerson(null);
  };

  // Handle delete person
  const handleDeletePerson = async (personId, personName) => {
    if (!window.confirm(`Are you sure you want to delete ${personName}?`)) {
      return;
    }

    const token = localStorage.getItem('authtoken');
    if (!token) {
      toast.error('Please sign in');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gifts/deleteGift/${personId}`, {
        method: 'DELETE',
        headers: { 'auth-token': token }
      });

      if (response.ok) {
        // Remove from local state
        setPeople(prev => prev.filter(p => p.id !== personId));
        setGifts(prev => prev.filter(g => g.id !== personId));
        toast.success(`${personName} deleted`);
      } else {
        toast.error('Failed to delete. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      toast.error('Failed to delete. Please try again.');
    }
  };

  // Handle edit person - open form with existing data
  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setSheetType('person');
    setSheetOpen(true);
  };

  // Handle gift form complete - save to database
  const handleGiftComplete = async (data) => {
    const token = localStorage.getItem('authtoken');
    if (!token) {
      toast.error('Please sign in to add gifts');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gifts/addgift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token
        },
        body: JSON.stringify({
          recipientName: data.recipientName || data.personName || 'Gift',
          relationship: data.relationship || '',
          occasion: data.occasion || 'gift',
          date: data.date || new Date().toISOString().split('T')[0],
          interests: '',
          budget: data.budget || '',
          comments: data.comments || '',
          // Gift-specific fields
          giftDescription: data.description || '',
          giftPrice: data.price || data.budget || '',
          giftStore: data.store || '',
          giftLink: data.link || '',
          giftGiven: true,
          giftGivenDate: data.date || new Date().toISOString().split('T')[0]
        })
      });

      const result = await response.json();
      if (result.success) {
        // Refresh data from server
        const giftsResponse = await fetch(`${API_URL}/api/gifts/getgifts`, {
          headers: { 'auth-token': token }
        });
        const giftsData = await giftsResponse.json();
        if (Array.isArray(giftsData)) {
          setPeople(giftsData);
          setGifts(giftsData);
        }

        setSheetOpen(false);
        setSheetType(null);

        toast.success('Gift saved! 🎁');
        triggerConfetti(50, 30);
      } else {
        toast.error('Failed to save gift. Please try again.');
      }
    } catch (error) {
      console.error('Error saving gift:', error);
      toast.error('Failed to save gift. Please try again.');
    }
  };

  // Close sheet
  const handleCloseSheet = () => {
    setSheetOpen(false);
    setSheetType(null);
    setViewingPerson(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Confetti />

      {/* Top Header with User Menu */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-white/95 via-white/90 to-white/95 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          {/* Premium Logo */}
          <div className="flex items-center gap-2.5">
            <LogoIcon size={36} animated />
            <h1 className="text-xl font-bold bg-gradient-to-r from-coral-600 via-rose-500 to-coral-500 bg-clip-text text-transparent">
              Giftedict
            </h1>
          </div>

          {/* User Menu */}
          <div className="relative user-menu-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setUserMenuOpen(!userMenuOpen);
              }}
              className="flex items-center gap-2 p-1.5 rounded-full hover:bg-slate-100/80 active:scale-95 transition-all duration-200"
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 via-coral-500 to-coral-600 flex items-center justify-center text-white font-semibold text-base shadow-lg shadow-coral-500/25 ring-2 ring-white">
                  {userName ? userName.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <svg
                className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <>
                {/* Backdrop */}
                <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />

                <div
                  className="absolute right-0 top-full mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50"
                  style={{
                    animation: 'dropdownIn 0.2s ease-out forwards',
                  }}
                >
                  {/* User Info Header */}
                  <div className="px-5 py-4 bg-gradient-to-br from-coral-50 via-white to-coral-50 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-400 via-coral-500 to-coral-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-coral-500/30">
                        {userName ? userName.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{userName || 'Guest'}</p>
                        <p className="text-sm text-slate-500 truncate">{userEmail || 'Not signed in'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    {/* Admin Dashboard Link - Only for admins */}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate('/admin');
                        }}
                        className="w-full px-5 py-3 text-left text-slate-700 hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 flex items-center gap-4 transition-all duration-150 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <span className="font-semibold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Admin Dashboard</span>
                          <p className="text-xs text-slate-400">Manage users & gifts</p>
                        </div>
                        <div className="ml-auto">
                          <span className="px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full">ADMIN</span>
                        </div>
                      </button>
                    )}

                  </div>

                  {/* Sign Out */}
                  <div className="p-2">
                    <button
                      onClick={handleSignOut}
                      className="w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-4 transition-all duration-150 group"
                    >
                      <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      <span className="font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Dropdown animation styles */}
      <style>{`
        @keyframes dropdownIn {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>

      {/* Celebration overlay */}
      <CelebrationOverlay
        active={showCelebration}
        title="Person Added!"
        message="You're on your way to being the most thoughtful gift-giver."
        onDismiss={() => setShowCelebration(false)}
      />

      {/* Main content with padding for bottom nav */}
      <div className="pb-24">
        {/* Home Tab */}
        {activeTab === 'home' && (
          <TimelineView
            userName={userName ? userName.split(' ')[0] : ''}
            upcomingOccasions={people}
            recentGifts={gifts}
            loading={loading}
            onAddPerson={handleFabClick}
            onFindGift={(occasion) => {
              toast(`Finding gifts for ${occasion.recipientName}...`);
            }}
            onViewPerson={(person) => {
              setViewingPerson(person);
              setSheetType('personDetail');
              setSheetOpen(true);
            }}
            onViewGift={(gift) => {
              toast(`Viewing gift: ${gift.description}`);
            }}
            isAdmin={isAdmin}
          />
        )}

        {/* People Tab */}
        {activeTab === 'people' && (
          <div className="px-4 py-6 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Your People</h2>
            {people.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-coral-100 flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl">💝</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No people yet</h3>
                <p className="text-slate-500 mb-6">Add the people you care about to never miss their special days</p>
                <button
                  onClick={handleFabClick}
                  className="px-6 py-3 bg-gradient-to-r from-coral-500 to-coral-600 text-white rounded-xl font-semibold shadow-lg shadow-coral-500/30 hover:shadow-xl transition-all"
                >
                  Add Your First Person
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {people.map((person) => (
                  <div
                    key={person.id}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral-400 to-coral-600 flex items-center justify-center text-white font-bold text-lg">
                        {person.recipientName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800">{person.recipientName}</h3>
                        <p className="text-sm text-slate-500 capitalize">{person.relationship} • {person.occasion}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">Next occasion</p>
                        <p className="text-sm font-medium text-coral-600">
                          {new Date(person.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <button
                        onClick={() => handleEditPerson(person)}
                        className="flex-1 py-2 px-3 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeletePerson(person.id, person.recipientName)}
                        className="flex-1 py-2 px-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <CalendarView people={people} />
        )}

        {/* Gifts Sent Tab - History of gifts given */}
        {activeTab === 'gifts-sent' && (
          <div className="px-4 py-6 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Gifts Sent</h2>
            {(() => {
              const sentGifts = gifts.filter(g => g.giftGiven === true);
              if (sentGifts.length === 0) {
                return (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <span className="text-4xl">🎁</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No gifts sent yet</h3>
                    <p className="text-slate-500">When you mark a gift as sent, it will appear here as part of your gift history</p>
                  </div>
                );
              }
              return (
                <div className="space-y-3">
                  {sentGifts.map((gift) => (
                    <div
                      key={gift.id}
                      className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-xl">
                          🎁
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 truncate">
                            {gift.giftDescription || 'Gift'}
                          </h3>
                          <p className="text-sm text-slate-500">
                            For {gift.recipientName}
                            {gift.occasion && ` • ${gift.occasion}`}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            {gift.giftPrice && (
                              <span className="flex items-center gap-1">
                                <span>💵</span> ${gift.giftPrice}
                              </span>
                            )}
                            {gift.giftStore && (
                              <span className="flex items-center gap-1">
                                <span>🏪</span> {gift.giftStore}
                              </span>
                            )}
                            {gift.giftGivenDate && (
                              <span className="flex items-center gap-1">
                                <span>📅</span> {new Date(gift.giftGivenDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                            ✓ Sent
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Bottom Navigation - Glass morphism style */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-slate-200 flex items-center justify-around px-4 py-2 safe-area-bottom z-50">
        <NavItem icon="🏠" label="Home" active={activeTab === 'home'} onClick={() => setActiveTab('home')} />
        <NavItem icon="💝" label="People" active={activeTab === 'people'} onClick={() => setActiveTab('people')} />
        <button
          onClick={handleFabClick}
          className="
            relative -mt-8
            w-14 h-14 rounded-full
            bg-gradient-to-br from-coral-500 to-coral-600 text-white
            flex items-center justify-center
            shadow-xl shadow-coral-500/40
            hover:shadow-2xl hover:shadow-coral-500/50 hover:scale-110
            active:scale-95
            transition-all duration-200
          "
          aria-label="Add new"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <NavItem icon="📅" label="Calendar" active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')} />
        <NavItem
          icon="🎁"
          label="Gifts sent"
          active={activeTab === 'gifts-sent'}
          onClick={() => setActiveTab('gifts-sent')}
        />
      </nav>

      {/* Bottom Sheet */}
      <BottomSheet
        isOpen={sheetOpen}
        onClose={handleCloseSheet}
        title={
          sheetType === 'person' ? 'Add Person' :
          sheetType === 'gift' ? 'Add Gift' :
          sheetType === 'discovery' ? '' :
          sheetType === 'personDetail' ? '' :
          'What would you like to add?'
        }
        snapPoints={sheetType && sheetType !== 'menu' ? ['95%'] : ['45%']}
      >
        {sheetType === 'menu' ? (
          <div className="space-y-3">
            <button
              onClick={handleAddPerson}
              className="
                w-full p-4 rounded-lg
                bg-coral-50 border-2 border-coral-200
                flex items-center gap-4
                hover:bg-coral-100 transition-colors
              "
            >
              <div className="w-12 h-12 rounded-full bg-coral-500 text-white flex items-center justify-center text-2xl">
                💝
              </div>
              <div className="text-left">
                <p className="font-medium text-warm-900">Add a Person</p>
                <p className="text-sm text-warm-500">Someone you want to remember</p>
              </div>
            </button>

            <button
              onClick={handleAddGift}
              className="
                w-full p-4 rounded-lg
                bg-gold-50 border-2 border-gold-200
                flex items-center gap-4
                hover:bg-gold-100 transition-colors
              "
            >
              <div className="w-12 h-12 rounded-full bg-gold-500 text-white flex items-center justify-center text-2xl">
                🎁
              </div>
              <div className="text-left">
                <p className="font-medium text-warm-900">Add a Gift</p>
                <p className="text-sm text-warm-500">Record something you gave</p>
              </div>
            </button>
          </div>
        ) : sheetType === 'person' ? (
          <PersonForm
            onComplete={handlePersonComplete}
            onCancel={() => {
              handleCloseSheet();
              setEditingPerson(null);
            }}
            initialData={editingPerson ? {
              recipientName: editingPerson.recipientName,
              relationship: editingPerson.relationship,
              relationshipDetail: editingPerson.relationshipDetail,
              occasion: editingPerson.occasion,
              date: editingPerson.date,
              interests: editingPerson.interests ? editingPerson.interests.split(', ') : [],
              budget: editingPerson.budget,
            } : {}}
            editId={editingPerson?.id}
          />
        ) : sheetType === 'gift' ? (
          <GiftForm
            onComplete={handleGiftComplete}
            onCancel={handleCloseSheet}
            people={people}
          />
        ) : sheetType === 'discovery' ? (
          <GiftDiscoverySheet
            person={selectedPerson}
            onClose={() => {
              handleCloseSheet();
              setSelectedPerson(null);
              setShowCelebration(true);
            }}
            onSelectGift={(gift) => {
              toast.success(`Great choice! We'll handle the rest.`);
              handleCloseSheet();
              setSelectedPerson(null);
            }}
          />
        ) : sheetType === 'personDetail' ? (
          <PersonDetailSheet
            person={viewingPerson}
            onClose={() => {
              handleCloseSheet();
              setViewingPerson(null);
            }}
          />
        ) : null}
      </BottomSheet>
    </div>
  );
}

// Beautiful Calendar View component
function CalendarView({ people }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  // Get occasions for a specific day
  const getOccasionsForDay = (day) => {
    return people.filter(person => {
      if (!person.date) return false;
      const occasionDate = new Date(person.date);
      return occasionDate.getDate() === day &&
             occasionDate.getMonth() === currentDate.getMonth() &&
             occasionDate.getFullYear() === currentDate.getFullYear();
    });
  };

  // Get all occasions for the current month
  const getOccasionsForMonth = () => {
    return people.filter(person => {
      if (!person.date) return false;
      const occasionDate = new Date(person.date);
      return occasionDate.getMonth() === currentDate.getMonth() &&
             occasionDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const today = new Date();
  const isToday = (day) => {
    return day === today.getDate() &&
           currentDate.getMonth() === today.getMonth() &&
           currentDate.getFullYear() === today.getFullYear();
  };

  const monthOccasions = getOccasionsForMonth();
  const selectedDayOccasions = selectedDate ? getOccasionsForDay(selectedDate) : [];

  // Occasion type colors
  const getOccasionColor = (occasion) => {
    const colors = {
      birthday: 'bg-pink-500',
      anniversary: 'bg-red-500',
      christmas: 'bg-green-500',
      graduation: 'bg-purple-500',
      wedding: 'bg-rose-500',
      'mother\'s day': 'bg-pink-400',
      'father\'s day': 'bg-blue-500',
      valentine: 'bg-red-400',
      other: 'bg-coral-500'
    };
    return colors[occasion?.toLowerCase()] || colors.other;
  };

  return (
    <div className="px-4 py-4 max-w-6xl mx-auto">
      {/* Split Screen Layout - Stack on mobile, side-by-side on desktop */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* LEFT: Calendar */}
        <div className="flex-1">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-800">Calendar</h2>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1 text-sm font-medium text-coral-600 bg-coral-50 rounded-lg hover:bg-coral-100 transition-colors"
            >
              Today
            </button>
          </div>

          {/* Calendar Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            {/* Month Navigation */}
            <div className="bg-gradient-to-r from-coral-500 to-rose-500 px-4 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-white">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                </div>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="py-2 text-center text-xs font-medium text-slate-400">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 p-1.5 gap-0.5">
              {/* Empty cells for days before the 1st */}
              {Array.from({ length: startingDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-9" />
              ))}

              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dayOccasions = getOccasionsForDay(day);
                const hasOccasions = dayOccasions.length > 0;
                const isSelected = selectedDate === day;

                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDate(isSelected ? null : day)}
                    className={`
                      h-9 rounded-lg flex flex-col items-center justify-center relative transition-all group
                      ${isToday(day) ? 'bg-coral-500 text-white' : ''}
                      ${isSelected && !isToday(day) ? 'bg-coral-100 ring-1 ring-coral-500' : ''}
                      ${!isSelected && !isToday(day) ? 'hover:bg-slate-100' : ''}
                    `}
                  >
                    <span className={`text-xs font-medium ${isToday(day) ? 'text-white' : isSelected ? 'text-coral-700' : 'text-slate-700'}`}>
                      {day}
                    </span>
                    {hasOccasions && (
                      <div className="flex gap-0.5">
                        {dayOccasions.slice(0, 2).map((occ, idx) => (
                          <div key={idx} className={`w-1 h-1 rounded-full ${isToday(day) ? 'bg-white' : getOccasionColor(occ.occasion)}`} />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected Day Details */}
            {selectedDate && selectedDayOccasions.length > 0 && (
              <div className="border-t border-slate-100 p-3 bg-coral-50/50">
                <h4 className="text-xs font-semibold text-slate-600 mb-2">
                  {monthNames[currentDate.getMonth()]} {selectedDate}
                </h4>
                <div className="space-y-1.5">
                  {selectedDayOccasions.map((person) => (
                    <div key={person.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                      <div className={`w-7 h-7 rounded-full ${getOccasionColor(person.occasion)} flex items-center justify-center text-white font-bold text-xs`}>
                        {person.recipientName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{person.recipientName}</p>
                        <p className="text-xs text-slate-500 capitalize">{person.occasion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: This Month's Occasions - Full width on mobile, fixed width on desktop */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <h4 className="font-semibold text-slate-700 text-sm flex items-center gap-1.5">
                📅 {monthNames[currentDate.getMonth()]}
                {monthOccasions.length > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 bg-coral-100 text-coral-600 text-xs font-bold rounded-full">
                    {monthOccasions.length}
                  </span>
                )}
              </h4>
            </div>

            {monthOccasions.length === 0 ? (
              <div className="p-4 text-center">
                <span className="text-2xl">🌟</span>
                <p className="text-slate-500 text-sm mt-1">No occasions this month</p>
              </div>
            ) : (
              <div className="p-2 lg:p-0 lg:divide-y lg:divide-slate-100 max-h-60 lg:max-h-80 overflow-y-auto">
                {/* Horizontal scroll on mobile, vertical list on desktop */}
                <div className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                  {monthOccasions.map((person) => {
                    const occasionDate = new Date(person.date);
                    const dayOfMonth = occasionDate.getDate();
                    const isPast = occasionDate < today && !isToday(dayOfMonth);

                    return (
                      <div
                        key={person.id}
                        className={`flex-shrink-0 lg:flex-shrink flex items-center gap-2 p-2 lg:p-2.5 bg-slate-50 lg:bg-transparent rounded-lg lg:rounded-none hover:bg-slate-100 transition-colors ${isPast ? 'opacity-60' : ''}`}
                      >
                        <div className="flex flex-col items-center w-8">
                          <span className="text-[10px] font-medium text-slate-400 uppercase">
                            {dayNames[occasionDate.getDay()].slice(0, 2)}
                          </span>
                          <span className={`text-lg font-bold ${isToday(dayOfMonth) ? 'text-coral-500' : 'text-slate-700'}`}>
                            {dayOfMonth}
                          </span>
                        </div>
                        <div className={`w-0.5 h-8 rounded-full ${getOccasionColor(person.occasion)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{person.recipientName}</p>
                          <p className="text-xs text-slate-500 capitalize truncate">{person.occasion}</p>
                        </div>
                        {isToday(dayOfMonth) && (
                          <span className="px-1.5 py-0.5 bg-coral-500 text-white text-[10px] font-bold rounded-full">
                            TODAY
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple nav item component with optional badge
function NavItem({ icon, label, active = false, onClick, badge = null }) {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center gap-1 p-2 rounded-lg
        ${active ? 'text-coral-500' : 'text-warm-400'}
        hover:text-coral-400 active:scale-95
        transition-all duration-150
      `}
    >
      <span className="relative text-xl">
        {icon}
        {badge && (
          <span className="absolute -top-1 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </span>
      <span className={`text-xs ${active ? 'font-medium' : ''}`}>{label}</span>
    </button>
  );
}

// Wrap with ToastProvider
export function NewHomePage() {
  return (
    <ToastProvider>
      <NewHomePageContent />
    </ToastProvider>
  );
}

export default NewHomePage;
